import axios from 'axios'

const DEFAULT_SCOPE = 'openid profile email'
const STATE_KEY = 'ops_keycloak_state'
const VERIFIER_KEY = 'ops_keycloak_verifier'
const RETURN_TO_KEY = 'ops_keycloak_return_to'
const PKCE_CHALLENGE_TIMEOUT_MS = 3000

function getConfig () {
  const url = process.env.VUE_APP_KEYCLOAK_URL
  const realm = process.env.VUE_APP_KEYCLOAK_REALM || 'telis'
  const clientId = process.env.VUE_APP_KEYCLOAK_CLIENT_ID || 'ops-app'

  return {
    authority: `${url}/realms/${realm}`,
    clientId,
    redirectUri: `${window.location.origin}/auth/callback`,
    postLogoutRedirectUri: window.location.origin,
    scope: process.env.VUE_APP_KEYCLOAK_SCOPE || DEFAULT_SCOPE
  }
}

function randomString (length) {
  if (!window.crypto || !window.crypto.getRandomValues) {
    throw new Error('WebCrypto random generator is unavailable')
  }

  const bytes = new Uint8Array(length)
  window.crypto.getRandomValues(bytes)
  return base64Url(bytes)
}

function base64Url (bytes) {
  const binary = Array.from(bytes).map(byte => String.fromCharCode(byte)).join('')
  return window.btoa(binary).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_')
}

async function createCodeChallenge (verifier) {
  if (!window.crypto || !window.crypto.subtle || typeof TextEncoder === 'undefined') {
    throw new Error('WebCrypto PKCE challenge is unavailable')
  }

  const data = new TextEncoder().encode(verifier)
  const digest = await Promise.race([
    window.crypto.subtle.digest('SHA-256', data),
    new Promise((resolve, reject) => {
      window.setTimeout(() => reject(new Error('WebCrypto PKCE challenge timed out')), PKCE_CHALLENGE_TIMEOUT_MS)
    })
  ])

  return base64Url(new Uint8Array(digest))
}

export async function loginWithKeycloak (returnTo = '/ops/home') {
  const config = getConfig()
  const state = randomString(24)
  const verifier = randomString(32)
  const challenge = await createCodeChallenge(verifier)
  const authorizeUrl = new URL(`${config.authority}/protocol/openid-connect/auth`)

  window.sessionStorage.setItem(STATE_KEY, state)
  window.sessionStorage.setItem(VERIFIER_KEY, verifier)
  window.sessionStorage.setItem(RETURN_TO_KEY, returnTo)

  authorizeUrl.searchParams.set('client_id', config.clientId)
  authorizeUrl.searchParams.set('redirect_uri', config.redirectUri)
  authorizeUrl.searchParams.set('response_type', 'code')
  authorizeUrl.searchParams.set('scope', config.scope)
  authorizeUrl.searchParams.set('state', state)
  authorizeUrl.searchParams.set('code_challenge', challenge)
  authorizeUrl.searchParams.set('code_challenge_method', 'S256')

  window.location.replace(authorizeUrl.toString())
}

function decodeJwtPayload (token) {
  try {
    const payload = token?.split('.')?.[1]
    if (!payload) return null
    const paddedPayload = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=')
    return JSON.parse(window.atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/')))
  } catch (e) {
    return null
  }
}

function mapTokenResponse (data, returnTo) {
  const payload = decodeJwtPayload(data.access_token)

  return {
    token: data.access_token,
    refreshToken: data.refresh_token,
    idToken: data.id_token,
    sid: typeof payload?.sid === 'string' ? payload.sid : null,
    sub: typeof payload?.sub === 'string' ? payload.sub : null,
    returnTo
  }
}

export async function completeKeycloakLogin (query) {
  const config = getConfig()
  const code = query.code
  const state = query.state
  const savedState = window.sessionStorage.getItem(STATE_KEY)
  const verifier = window.sessionStorage.getItem(VERIFIER_KEY)
  const returnTo = window.sessionStorage.getItem(RETURN_TO_KEY) || '/ops/home'

  if (!code || !state || state !== savedState || !verifier) {
    throw new Error('Invalid Keycloak callback')
  }

  const body = new URLSearchParams()
  body.append('grant_type', 'authorization_code')
  body.append('client_id', config.clientId)
  body.append('code', code)
  body.append('redirect_uri', config.redirectUri)
  body.append('code_verifier', verifier)

  const response = await axios.post(
    `${config.authority}/protocol/openid-connect/token`,
    body,
    {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      skipAuthRefresh: true
    }
  )

  window.sessionStorage.removeItem(STATE_KEY)
  window.sessionStorage.removeItem(VERIFIER_KEY)
  window.sessionStorage.removeItem(RETURN_TO_KEY)

  return mapTokenResponse(response.data, returnTo)
}

export async function refreshKeycloakSession (refreshToken) {
  const config = getConfig()
  const body = new URLSearchParams()

  body.append('grant_type', 'refresh_token')
  body.append('client_id', config.clientId)
  body.append('refresh_token', refreshToken)

  const response = await axios.post(
    `${config.authority}/protocol/openid-connect/token`,
    body,
    {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      skipAuthRefresh: true
    }
  )

  return mapTokenResponse(response.data)
}

export function logoutFromKeycloak (idTokenHint) {
  const config = getConfig()
  const logoutUrl = new URL(`${config.authority}/protocol/openid-connect/logout`)

  logoutUrl.searchParams.set('client_id', config.clientId)
  logoutUrl.searchParams.set('post_logout_redirect_uri', config.postLogoutRedirectUri)
  if (idTokenHint) logoutUrl.searchParams.set('id_token_hint', idTokenHint)

  return logoutUrl.toString()
}
